import React, { useEffect, useRef, useState } from 'react';

import './pie_chart.component.css';

const PieChart = (props) => {
  /**
   * props:
   *    chartId: id for the div to draw chart in
   *    categoryLabel: label for category
   *    valueLabel: label for value
   *    data: [{name: <string:name>, value: <number:value>}]
   *    tootltipValueLabel(optional): label for value tooltip
   */
  const chartRef = useRef(null);
  const {
    portfolioId,
    chartId,
    categoryLabel,
    valueLabel,
    data,
    refs,
    setRefs,
    redraw,
    getTooltip,
    setChartCurrentLevel,
  } = props;

  //states
  const [currentBreakupLevel, setCurrentBreakupLevel] = useState([]);
  const [currentBreadcrumbs, setCurrentBreadcrumbs] = useState([]);
  const addClasstoDrillable = useRef((e) => {
    $('g:contains(→)').addClass('chart-drillable-label');
  });

  const setCurrentBreakupLevelwithRef = (level) => {
    setRefs('refCurrentBreakupLevel', level);
    setCurrentBreakupLevel(level);
  };

  // Effects
  useEffect(() => {
    const pieChart = new google.visualization.PieChart(
      document.getElementById(chartId),
    );

    google.visualization.events.addListener(pieChart, 'click', (e) => {
      const level = e.targetID.split('#')[1];
      if (level) {
        updateLevel(parseInt(level));
      }
    });

    window.addEventListener('resize', drawChart, false);
    chartRef.current = pieChart;
    drawChart();

    google.visualization.events.addListener(
      chartRef.current,
      'onmouseover',
      () => {
        addClasstoDrillable.current();
      },
    );
    google.visualization.events.addListener(
      chartRef.current,
      'onmouseout',
      () => {
        addClasstoDrillable.current();
      },
    );
  }, [redraw, props.onScreen]);

  useEffect(() => {
    drawChart();
  }, [currentBreakupLevel]);

  useEffect(() => {
    if (currentBreadcrumbs && setChartCurrentLevel)
      setChartCurrentLevel(currentBreadcrumbs);
  }, [currentBreadcrumbs]);

  useEffect(() => {
    if (chartId === 'product_chart') return;
    //drilldown by default if only one instrument
    const isEmptyInst = data.filter((item) => !!item.weight).length === 1;
    if (data && data.length && isEmptyInst) {
      setCurrentBreakupLevelwithRef([data.findIndex((item) => !!item.weight)]);
    }
  }, []);

  // Functions
  const createEmptyDataTable = () => {
    var dataTable = new google.visualization.DataTable();
    dataTable.addColumn('string', categoryLabel);
    dataTable.addColumn('number', valueLabel);
    dataTable.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
    return dataTable;
  };

  const getRowsAtCurrentLevel = () => {
    let rows = [];
    let currWeight;
    let currLevel = _.cloneDeep(data);
    let breadCrumbs = [];

    if (refs[portfolioId] && refs[portfolioId].refCurrentBreakupLevel) {
      refs[portfolioId].refCurrentBreakupLevel.forEach((l) => {
        const levelItem = currLevel[l];

        if (levelItem) {
          breadCrumbs.push(
            levelItem.category_name || levelItem.name || 'Unnamed Category',
          );
          currWeight = levelItem.weight;
          currLevel = levelItem.children ? levelItem.children : [];
        } else {
          console.warn('Undefined levelItem at index:', l);
        }
      });
    }

    setCurrentBreadcrumbs(breadCrumbs);
    if (chartId === 'product_chart') {
      rows = currLevel.map((c) => {
        let companies = '';
        let sortedCompanies = c.companies.sort((a, b) => b[1] - a[1]);
        sortedCompanies
          .slice(0, 5)
          .forEach(
            (cmp) =>
              (companies += `<div>${cmp[0]}: <b>${cmp[1].toFixed(
                2,
              )}%</b></div>`),
          );
        return [
          c.category_name,
          c.weight,
          `<div class="chart-tooltip p-3">
            <div class="chart-tooltip-title">${c.category_name}</div>
            <span class="chart-tooltip-value-label">% of Total Revenue:</span>
            <span class="chart-tooltip-value">${c.weight.toFixed(2)}%</span>
            <div class="chart-tooltip-extra pt-2 mt-2">
              <div class="chart-tooltip-extra-header">Companies weighted exposure</div>
              ${companies}
            </div>
          </div>`,
        ];
      });
      const childWeightTotal = currLevel.reduce((prev, curr) => {
        return curr.weight + prev;
      }, 0);
      const diffWeight = currWeight - childWeightTotal;
    } else {
      rows = currLevel.map((c) => {
        return [
          c.children && c.children.length ? `${c.name}→` : c.name,
          c.weight,
          `<div class="chart-tooltip px-2 py-2">
          <div class="chart-tooltip-title">${c.name}</div>
          <span class="chart-tooltip-value-label">${valueLabel}</span>
          <span class="chart-tooltip-value">${c.weight.toFixed(2)}%</span>
        </div>`,
        ];
      });
    }

    return rows;
  };

  const updateLevel = (level, remove) => {
    let bkupLevel = _.cloneDeep(refs[portfolioId].refCurrentBreakupLevel);
    let currLevel = _.cloneDeep(data);
    let setLevel;
    if (remove) {
      // remove the last element
      setLevel = bkupLevel.slice(0, -1);
    } else {
      setLevel = [...bkupLevel, level];
      // Follow the breadcrumbs
      setLevel.forEach((l) => {
        currLevel = currLevel[l].children;
      });
      // Don't update bkupLevel if the current level is empty
      if (!currLevel || currLevel.length == 0) return;
    }
    setCurrentBreakupLevelwithRef(setLevel);
  };

  const getChartOptions = () => {
    const width = window.innerWidth;
    let legendPosition = 'labeled';
    let sliceText = 'none';
    let chartDim = {
      width: '100%',
      height: '100%',
    };
    let fontSize = 15;
    if (width < 310) {
      legendPosition = 'right';
      sliceText = 'label-and-percentage';
    }
    if (width < 992) {
      chartDim = {
        width: '100%',
        height: 'auto',
      };
      fontSize = 14;
    }
    const options = {
      legend: {
        position: legendPosition,
        alignment: 'center',
        textStyle: { fontSize: 14 },
      },
      animation: {
        startup: true,
        duration: 1000,
        easing: 'inAndOut',
      },
      tooltip: { isHtml: true },
      pieSliceText: sliceText,
      is3D: true,
      chartArea: chartDim,
      fontSize,
      enableInteractivity: true,
      sliceVisibilityThreshold: 0.000001,
      colors: [
        '#1094BC',
        '#EBCB38',
        '#F36F14',
        '#4A78BB',
        '#07A499',
        '#06A14E',
        '#037C57',
        '#C13018',
        '#E61359',
        '#ED95BE',
        '#628689',
        '#6AC0BC',
        '#7DC398',
        '#BED997',
        '#F7EF96',
        '#F5C085',
        '#DA7F67',
        '#B788B9',
        '#867EB7',
        '#89C13C',
        '#497774',
        '#88AA93',
        '#E6F79D',
        '#FFC255',
        '#EC6B56',
      ],
    };
    return options;
  };

  const drawChart = () => {
    if (chartRef.current) {
      const options = getChartOptions();
      const dataTable = createEmptyDataTable();
      const rows = getRowsAtCurrentLevel();
      dataTable.addRows(rows);
      chartRef.current.draw(dataTable, options);
      //had to do this
      addClasstoDrillable.current();
    }
  };

  return (
    <div className="position-relative my-5">
      <div className="charts-breadCrumbs">
        {currentBreadcrumbs.length > 0 && (
          <span
            onClick={() => setCurrentBreakupLevelwithRef([])}
            className="pointer chart-breadcrumbs chart-breadcrumbs-all"
          >
            All
          </span>
        )}
        {currentBreadcrumbs &&
          currentBreadcrumbs.map((bc, idx) => (
            <React.Fragment key={bc}>
              <span className="nesting-arrow">{' > '}</span>
              <span
                onClick={() =>
                  setCurrentBreakupLevelwithRef(
                    currentBreakupLevel.slice(0, idx + 1),
                  )
                }
                className="pointer chart-breadcrumbs"
              >
                {bc}
              </span>
            </React.Fragment>
          ))}
      </div>
      <div id={chartId} className={props.className ?? ''}></div>
      {chartId !== 'product_chart' && !currentBreakupLevel.length && (
        <div className="charts-info-text">
          *Click on any instrument to drill deeper
        </div>
      )}
    </div>
  );
};

export default PieChart;
